import { ResultStatus } from './test-run.model';
import { formatElapsedClock } from '../shared/duration.util';

/**
 * Woher ein Eintrag der Sektion „Pipelines · Live" stammt (NEX-1290). `TRIGGERED` hat
 * testOFFICE selbst ausgeloest und fuehrt deshalb einen Testlauf; `OBSERVED` laeuft im
 * angebundenen Repository, ohne dass jemand hier darauf gedrueckt haette.
 *
 * Die Liste zur Laufzeit steht neben dem Typ, weil der Wert aus JSON kommt.
 */
export const PIPELINE_ORIGINS = ['TRIGGERED', 'OBSERVED'] as const;
export type PipelineOrigin = (typeof PIPELINE_ORIGINS)[number];

/**
 * Wofuer die Punkte eines Eintrags stehen: `TESTS` sind einzelne Tests, `JOBS` die Jobs der
 * Pipeline. Ein beobachteter Lauf zeigt Tests, sobald seine Pipeline sie live meldet — die
 * Herkunft allein sagt deshalb nicht mehr, wofuer ein Punkt steht.
 */
export const SEGMENT_KINDS = ['TESTS', 'JOBS'] as const;
export type SegmentKind = (typeof SEGMENT_KINDS)[number];

/**
 * Ein Punkt der Leiste, wie ihn das Backend liefert: ein Test oder ein Job der Pipeline,
 * je nach {@link segmentKindOf}.
 */
export interface LivePipelineSegment {
  label?: string;
  status: ResultStatus;
}

/**
 * Ein Durchlauf aus Repository-Quelle, wie ihn `GET /api/v1/projects/{id}/pipelines/live`
 * und das Projekt-Topic liefern (NEX-1230). Optionale Felder fehlen in der Antwort,
 * statt `null` zu tragen — das Backend laesst leere Werte weg.
 *
 * `runId` und `runKey` stehen auch an einem beobachteten Lauf: der Beobachter haelt ihn als
 * Testlauf fest, sobald er ihn zuerst sieht. Sie fehlen nur, wenn das nicht gelang.
 * `executionId` fehlt bei ihm immer — sein Durchlauf entsteht erst am Lauf-Ende (NEX-1334).
 * Woher der Lauf stammt, sagt `origin`; wofuer seine Punkte stehen, `segmentKind`.
 */
export interface LivePipelineEntry {
  runId?: string;
  runKey?: string;
  executionId?: string;
  repository?: string;
  workflow?: string;
  branch?: string;
  commit?: string;
  projectName?: string;
  origin: PipelineOrigin;
  /** Fehlt, solange das Backend das Feld nicht liefert; dann gilt {@link segmentKindOf}. */
  segmentKind?: SegmentKind;
  /**
   * Der Lauf beim Anbieter. Kein Ziel der Karte — dessen Seite sieht ein Betrachter ohne Zugang
   * zum Repository als 404 —, sondern die Kennung eines beobachteten Laufs in `livePipelineKey`.
   */
  externalRunUrl?: string;
  segments: LivePipelineSegment[];
  currentTest?: LivePipelineCurrentTest;
  startedAt: string;
  /** Nur an einem beendeten Durchlauf; fehlt bei aelteren Staenden und Laeufen ohne bekanntes Ende. */
  finishedAt?: string;
  status: ResultStatus;
}

export interface LivePipelineCurrentTest {
  automationKey?: string;
  name?: string;
}

/**
 * Der Eintrag mit dem Projekt, aus dessen Endpunkt er stammt. Die Antwort nennt das
 * Projekt nur beim Namen; die Kennung braucht die Karte fuer den Absprung in die
 * Durchlaeufe und kennt sie aus der eigenen Anfrage.
 */
export interface LivePipeline extends LivePipelineEntry {
  projectId: string;
}

/** Der Stand eines Durchlaufs in Zahlen, gezaehlt aus seinen Punkten. */
export interface LivePipelineCounts {
  total: number;
  /** Punkte mit endgueltigem Ergebnis — auch uebersprungene und blockierte. */
  done: number;
  passed: number;
  failed: number;
}

/**
 * Was die Punkt-Leiste von einem Durchlauf braucht: seine Punkte und ob er noch laeuft.
 * Bewusst schmaler als der ganze Eintrag — die Leiste steht unter `components/` und
 * zeichnet, was man ihr gibt; vom Rest des Eintrags weiss sie nichts.
 */
export type PipelineProgress = Pick<LivePipelineEntry, 'segments' | 'status'>;

const FINAL_STATUSES: readonly ResultStatus[] = ['PASSED', 'FAILED', 'BLOCKED', 'SKIPPED'];

export function pipelineCounts(entry: PipelineProgress): LivePipelineCounts {
  const segments = entry.segments;
  return {
    total: segments.length,
    done: segments.filter((segment) => FINAL_STATUSES.includes(segment.status)).length,
    passed: segments.filter((segment) => segment.status === 'PASSED').length,
    failed: segments.filter((segment) => segment.status === 'FAILED').length,
  };
}

/**
 * Der Punkt, an dem der Durchlauf gerade arbeitet; {@code -1}, sobald er an keinem mehr
 * arbeitet. Meldet das Backend keinen laufenden Punkt, gilt der erste ohne endgueltiges
 * Ergebnis — dieselbe Regel, nach der das Backend den laufenden Testfall waehlt.
 */
export function runningSegmentIndex(entry: PipelineProgress): number {
  if (entry.status !== 'RUNNING') {
    return -1;
  }
  const running = entry.segments.findIndex((segment) => segment.status === 'RUNNING');
  return running >= 0 ? running : entry.segments.findIndex((segment) => !FINAL_STATUSES.includes(segment.status));
}

/**
 * Die Position des laufenden Punktes, gezaehlt ab dem ersten. Der letzte Punkt bleibt
 * der laufende, statt hinter die Gesamtzahl zu zaehlen: sein Ergebnis liegt erst vor,
 * wenn der Durchlauf endet, und bis dahin arbeitet er daran.
 */
export function runningSegmentPosition(entry: PipelineProgress): number {
  const index = runningSegmentIndex(entry);
  if (index >= 0) {
    return index + 1;
  }
  const { done, total } = pipelineCounts(entry);
  return Math.min(done + 1, total);
}

/**
 * Wofuer die Punkte eines Eintrags stehen. Fehlt das Feld, gilt, was vor ihm galt: ein
 * beobachteter Lauf zeigt Jobs, ein ausgeloester Tests. Die einzige Stelle dieses
 * Rueckfalls — die Leser fragen hier und nie mehr nach `origin`.
 *
 * <p>Ein unbekannter Wert zaehlt wie ein fehlender: er kommt ungeprueft aus JSON, und ohne
 * passenden Text wirft die Uebersetzung der Leiste.
 */
export function segmentKindOf(entry: Pick<LivePipelineEntry, 'segmentKind' | 'origin'>): SegmentKind {
  const kind = entry.segmentKind;
  if (kind && SEGMENT_KINDS.includes(kind)) {
    return kind;
  }
  return entry.origin === 'OBSERVED' ? 'JOBS' : 'TESTS';
}

/**
 * Woran der Durchlauf gerade arbeitet: bei Tests der laufende Test, bei Jobs der laufende
 * Job ({@link segmentKindOf}).
 *
 * <p>{@code null}, sobald der Durchlauf an nichts mehr arbeitet. Ob eine Stelle ohne
 * Namen noch gezeigt wird, entscheidet der Leser. Eine Stelle fuer beide — zwei Ableitungen
 * nebeneinander waren bereits darin auseinandergelaufen, ob ein beendeter Durchlauf
 * noch etwas Laufendes zeigt.
 *
 * <p>Ohne laufenden Job wird keiner genannt: die Jobs stehen einzeln in der Antwort, und
 * der letzte fertige waere der falsche — die Karte zeigte ihn mit Spinner, waehrend die
 * Leiste denselben Punkt gruen und ohne Puls zeichnet. Bei Tests bleibt der Rueckfall:
 * dort nennt das Backend den laufenden Test eigens, und die Position zaehlt die
 * eingelaufenen Ergebnisse — auch zwischen zwei Tests, wenn gerade keiner laeuft.
 */
export interface RunningWork {
  index: number;
  total: number;
  name: string;
  automationKey?: string;
}

export function runningWork(entry: LivePipelineEntry): RunningWork | null {
  if (entry.status !== 'RUNNING' || entry.segments.length === 0) {
    return null;
  }
  const total = entry.segments.length;
  if (segmentKindOf(entry) === 'JOBS') {
    const running = runningSegmentIndex(entry);
    return running < 0 ? null : { index: running + 1, total, name: entry.segments[running].label ?? '' };
  }
  const currentTest = entry.currentTest;
  const index = runningSegmentPosition(entry);
  return { index, total, name: currentTest?.name ?? '', automationKey: currentTest?.automationKey };
}

/**
 * Die Kennung, unter der ein Eintrag gefuehrt wird. Ein beobachteter Lauf hat keinen
 * Durchlauf; der Link auf den Lauf beim Anbieter ist dann der Wert, den beide Seiten
 * fuehren und an dem das Backend denselben Lauf wiedererkennt. Einen Eintrag ohne beides
 * gibt es nicht — faellt doch einer herein, faellt er mit seinesgleichen zusammen, statt
 * sich hinter einem Zeitstempel zu verstecken, der zwei Laeufe verwechseln kann.
 */
export function livePipelineKey(entry: LivePipelineEntry): string {
  return entry.executionId ?? entry.externalRunUrl ?? '';
}

export function isPipelineFinished(entry: Pick<LivePipelineEntry, 'status'>): boolean {
  return FINAL_STATUSES.includes(entry.status);
}

/**
 * Die gezeigten Eintraege der Sektion „Pipelines · Live“: nur laufende, neueste zuerst.
 * Beendete und wartende Laeufe liefert der Endpunkt weiterhin; sie gehoeren in die Testlaeufe.
 */
export function runningLivePipelines(pipelines: LivePipeline[]): LivePipeline[] {
  return pipelines
    .filter((entry) => entry.status === 'RUNNING')
    .sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt));
}

/** Die Gesamtdauer eines beendeten Durchlaufs im Format der Laufzeit; {@code null} ohne gueltiges Ende. */
export function finishedDuration(entry: Pick<LivePipelineEntry, 'startedAt' | 'finishedAt'>): string | null {
  if (!entry.finishedAt) {
    return null;
  }
  const duration = Date.parse(entry.finishedAt) - Date.parse(entry.startedAt);
  return Number.isNaN(duration) || duration < 0 ? null : formatElapsedClock(duration);
}
